import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { api, divisionOrder, divisionMeta, gradeLabel } from "@/lib/api";
import { ArrowRight, Search, Pencil } from "lucide-react";
import GroupEditDialog from "@/components/GroupEditDialog";

export default function GroupsPage() {
  const [params, setParams] = useSearchParams();
  const activeDivision = params.get("division") || "All";
  const [q, setQ] = useState("");

  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => (await api.get("/settings")).data,
    staleTime: 60_000,
  });
  const seasonYear = settings?.season_year || new Date().getFullYear();

  const { data: groups = [], isLoading } = useQuery({
    queryKey: ["groups"],
    queryFn: async () => (await api.get("/groups")).data,
  });

  const filtered = useMemo(() => {
    return groups.filter((g) => {
      const okDiv = activeDivision === "All" || g.division === activeDivision;
      const term = q.trim().toLowerCase();
      if (!term) return okDiv;
      const hay = [
        g.code,
        g.division,
        g.subgroup,
        g.group_leader,
        g.head_counselor,
        ...(g.assistants || []).map((a) => a.name),
        ...(g.cits || []).map((c) => c.name),
      ]
        .join(" ")
        .toLowerCase();
      return okDiv && hay.includes(term);
    });
  }, [groups, activeDivision, q]);

  return (
    <div data-testid="groups-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <div className="font-mono-camp text-[11px] tracking-widest text-[#13315C]/60 uppercase">
            Tip Letters
          </div>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-[#0B2545]">
            Group Letters
          </h1>
          <p className="text-[#13315C]/75 mt-2 max-w-xl text-sm sm:text-base">
            Each group gets one tip letter. Tap <b>Edit</b> to change any
            counselor name or grade, or <b>Letter</b> to preview &amp; print.
          </p>
        </div>

        <div className="relative w-full md:w-auto">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#13315C]/50" />
          <input
            data-testid="group-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search group, counselor, or CIT…"
            className="w-full md:w-80 pl-9 pr-3 py-2.5 rounded-full border border-[#E9DDC8] bg-white focus:outline-none focus:ring-2 focus:ring-[#0B2545]/25 text-sm"
          />
        </div>
      </div>

      {/* Division filter */}
      <div className="flex flex-wrap gap-2 mb-8">
        {["All", ...divisionOrder].map((d) => {
          const active = activeDivision === d;
          return (
            <button
              key={d}
              data-testid={`filter-${d.toLowerCase()}`}
              onClick={() => {
                const next = new URLSearchParams(params);
                if (d === "All") next.delete("division");
                else next.set("division", d);
                setParams(next);
              }}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold border transition-colors ${
                active
                  ? "bg-[#0B2545] text-[#FDF6EC] border-[#0B2545]"
                  : "bg-white text-[#0B2545] border-[#E9DDC8] hover:border-[#0B2545]"
              }`}
            >
              {d === "All" ? "All divisions" : divisionMeta[d].label}
            </button>
          );
        })}
      </div>

      {isLoading ? (
        <div className="text-[#13315C]/60" data-testid="groups-loading">
          Loading groups…
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((g) => (
            <GroupCard key={g.code} group={g} />
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full text-[#13315C]/60 italic">
              No groups match your filters.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function GroupCard({ group }) {
  const [editOpen, setEditOpen] = useState(false);
  const meta = divisionMeta[group.division] || {};
  const asstCollege = (group.assistants || []).filter(
    (a) => String(a.grade || "").toUpperCase() === "SR+"
  ).length;
  const asstHS = (group.assistants || []).length - asstCollege;

  return (
    <>
      <div
        data-testid={`group-card-${group.code}`}
        className="tile paper-card p-5 flex flex-col"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="font-mono-camp text-[11px] tracking-widest text-[#13315C]/60 uppercase">
              {group.subgroup || meta.label}
            </div>
            <div className="font-display text-3xl font-bold text-[#0B2545] leading-none mt-1">
              {group.code}
            </div>
          </div>
          <span className={`tag ${meta.accent || "navy"}`}>
            {meta.label || group.division}
          </span>
        </div>

        <div className="mt-4 space-y-1.5 text-sm">
          <div className="flex gap-2">
            <span className="text-[#13315C]/60 w-20 shrink-0">Head</span>
            <span
              className="font-semibold text-[#0B2545]"
              data-testid={`group-${group.code}-head`}
            >
              {group.head_counselor || "—"}
            </span>
          </div>
          {(group.assistants || []).slice(0, 4).map((a, i) => (
            <div className="flex gap-2" key={i}>
              <span className="text-[#13315C]/60 w-20 shrink-0">
                {gradeLabel(a.grade)}
              </span>
              <span className="text-[#0B2545]">{a.name}</span>
            </div>
          ))}
          {(group.assistants || []).length > 4 && (
            <div className="text-[#13315C]/50 italic text-xs pl-22">
              + {group.assistants.length - 4} more…
            </div>
          )}
          {(group.cits || []).map((c, i) => (
            <div className="flex gap-2" key={"cit" + i}>
              <span className="text-[#13315C]/60 w-20 shrink-0">CIT</span>
              <span className="text-[#0B2545]">{c.name}</span>
            </div>
          ))}
        </div>

        <div className="mt-5 pt-4 border-t border-dashed border-[#E9DDC8] flex items-center justify-between">
          <div className="flex gap-1.5">
            {asstCollege > 0 && <span className="tag teal">{asstCollege} College</span>}
            {asstHS > 0 && <span className="tag gold">{asstHS} HS</span>}
            {(group.cits || []).length > 0 && (
              <span className="tag coral">{group.cits.length} CIT</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setEditOpen(true)}
              data-testid={`edit-group-${group.code}`}
              className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-full border border-[#0B2545] text-[#0B2545] hover:bg-[#0B2545] hover:text-[#FDF6EC]"
            >
              <Pencil className="w-3 h-3" /> Edit
            </button>
            <Link
              to={`/letter/group/${group.code}`}
              data-testid={`group-${group.code}-letter`}
              className="inline-flex items-center text-sm font-semibold text-[#0B2545] hover:underline"
            >
              Letter <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </div>
        </div>
      </div>

      <GroupEditDialog group={group} open={editOpen} onOpenChange={setEditOpen} />
    </>
  );
}
